import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   Grid,
   GridItem,
   useColorModeValue,
   Box,
   Divider,
   Text,
   VStack,
   HStack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { renderDynamicDropdown } from '../../../../../components/UIcomponents/DynamicDropdown';
import InputField from 'components/ui/InputField';
import { splitPayload } from 'utils/saleUtility';
import calculateTaxAmount from 'utils/saleUtility';
import { getAllDaybooks } from '../../../../../redux/actions/daybook';
import { useUser, useGlobal } from '../../../../../contexts';
import useIssueLastDaybook from '../../../../../hooks/useIssueLastDaybook';
import { addManyIssueInventory } from '../../../../../redux/actions/issueInventory';

// ==================== Constants ====================
const DAYBOOK_TYPE_ISSUE = 19;
const DATE_FORMAT = 'YYYY-MM-DD';
const FOCUS_DELAY_MS = 100;
const DEFAULT_CHALLAN_NUMBER = '1';

const ORDER_TYPES = Object.freeze({
   NORMAL: 0,
   ASCENDING: 1,
   DESCENDING: 2,
});

const ORDER_DATA = [
   { label: 'Normal', value: ORDER_TYPES.NORMAL, id: ORDER_TYPES.NORMAL },
   { label: 'Ascending', value: ORDER_TYPES.ASCENDING, id: ORDER_TYPES.ASCENDING },
   { label: 'Descending', value: ORDER_TYPES.DESCENDING, id: ORDER_TYPES.DESCENDING },
];

const UOM_NUMBERS = Object.freeze({
   FIRST: 1,
   SECOND: 2,
   THIRD: 3,
});

const INITIAL_FORM_STATE = {
   daybookId: '',
   noofchallan: 1,
   challanno: '',
   issueAt: moment().local().format(DATE_FORMAT),
   millId: null,
   orderId: null,
   brokerId: null,
   jobrate: '',
   remarks: '',
};

const EMPTY_FORM_STATE = {
   daybookId: null,
   noofchallan: '',
   challanno: '',
   issueAt: '',
   millId: null,
   orderId: 0,
   brokerId: null,
   jobrate: '',
   remarks: '',
};

// ==================== Helper Functions ====================

/**
 * Determines the UOM number based on the rate per purchase ID
 * @param {Object} selectedItem - The selected item with UQC and rate per purchase information
 * @returns {number} The UOM number (1, 2, or 3)
 */
const getUomNoForRate = (selectedItem) => {
   if (!selectedItem) return UOM_NUMBERS.FIRST;

   const { uqcid1, uqcid2, uqcid3, rateperpurchaseid } = selectedItem;

   if (uqcid1 === rateperpurchaseid) return UOM_NUMBERS.FIRST;
   if (uqcid2 === rateperpurchaseid) return UOM_NUMBERS.SECOND;
   if (uqcid3 === rateperpurchaseid) return UOM_NUMBERS.THIRD;

   return UOM_NUMBERS.FIRST;
};

/**
 * Sorts taka details based on order type
 * @param {Array} purchaseLineDetail - Array of purchase line details
 * @param {number} orderId - Order type ID
 * @returns {Array} Sorted array of taka details
 */
const sortTakaDetails = (purchaseLineDetail, orderId) => {
   if (!purchaseLineDetail) return [];

   switch (orderId) {
      case ORDER_TYPES.ASCENDING:
         return [...purchaseLineDetail].sort((a, b) => (a.quantity3 ?? 0) - (b.quantity3 ?? 0));
      case ORDER_TYPES.DESCENDING:
         return [...purchaseLineDetail].sort((a, b) => (b.quantity3 ?? 0) - (a.quantity3 ?? 0));
      default:
         return purchaseLineDetail;
   }
};

/**
 * Builds a line item object from issue data
 * @param {Object} item - The line item data
 * @param {Object} selectedItem - The selected item from master data
 * @param {number} jobrate - The job rate
 * @param {number} orderId - The order type ID
 * @returns {Object} Formatted line item object
 */
const buildLineItem = (item, selectedItem, jobrate, orderId) => {
   const takaDetails = sortTakaDetails(item.purchaseLineDetail, orderId);

   return {
      pliId: item.id,
      typeOfSupply: item.typeOfSupply || 1,
      itemId: Number(item.itemId) || 0,
      lineNumber: Number(item.lineNumber) || 1,
      itemRate: Number(jobrate ?? item?.itemRate) || 0,
      amount: Number(item.amount) || 0,
      netAmount: Number(item.netAmount) || 0,
      ratePer: Number(selectedItem?.rateperpurchaseid) || 0,
      uomNoForRate: getUomNoForRate(selectedItem),
      qUnitId1: selectedItem?.uqcid1,
      qUnitId2: selectedItem?.uqcid2,
      qUnitId3: selectedItem?.uqcid3,
      qty1: Number(item.qty1 ?? 0),
      qty2: Number(item.qty2 ?? 0),
      qty3: Number(item.qty3 ?? 0),
      addLess1Percent: Number(item.addLess1Percent) || 0,
      addLess1Amount: item.addLess1Amount || 0,
      addLess2Percent: Number(item.addLess2Percent) || 0,
      addLess2Amount: item.addLess2Amount || 0,
      addLess3Percent: Number(item.addLess3Percent) || 0,
      addLess3Amount: item.addLess3Amount || 0,
      taxableAmount: item.taxableAmount || 0,
      netGst: item.netGst || 0,
      gstPercent: item.gst || 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      stock: item.stock?.length > 0 ? item.stock : [],
      takaDetails: takaDetails?.length > 0
         ? takaDetails.map(({ issueLineDetailId, maxQty1, maxQty2, maxQty3, minQty1, minQty2, minQty3, purchaseStatus, specifications, warehouseName, ...rest }) => {
            const { id, ...subrest } = rest;
            return {
               ...subrest,
               pinvLineDetailId: rest.id,
            };
         }) : [],
      hsnId: selectedItem?.hsnid,
      sacId: selectedItem?.sacid,
      isDeleted: false,
   };
};

// ==================== Main Component ====================

/**
 * IssueToMill Modal Component
 * Handles the creation of issue to mill inventory transactions
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Array} props.brokerData - List of broker/mill accounts
 * @param {Object} props.issueData - Issue transaction data
 */
const IssueToMill = ({ isOpen, onClose, brokerData, issueData }) => {
   // ==================== Hooks ====================
   const dispatch = useDispatch();
   const navigate = useNavigate();

   const daybookData = useSelector((state) => state.daybook?.allDaybooks || []);
   const billTermData = useSelector((state) => state.billTerm?.list);
   const itemData = useSelector((state) => state.item?.list ?? []);

   const { user } = useUser();
   const { company } = useGlobal();
   const { lastDaybook } = useIssueLastDaybook();

   // ==================== State ====================
   const [rows, setRows] = useState({
      ...INITIAL_FORM_STATE,
      daybookId: lastDaybook?.daybookId ?? '',
   });
   const [isLoading, setIsLoading] = useState(false);

   // ==================== Refs ====================
   const submitRef = useRef(null);
   const daybookRef = useRef(null);
   const docNoRef = useRef(null);
   const transactionAtRef = useRef(null);
   const remarksRef = useRef(null);
   const jobrateRef = useRef(null);
   const brokerRef = useRef(null);
   const orderRef = useRef(null);
   const challanNoRef = useRef(null);
   const millRef = useRef(null);

   // ==================== Memoized Values ====================
   const textColor = useColorModeValue('gray.800', 'white');

   const filteredDaybookData = useMemo(
      () => daybookData.filter((item) => item.type === DAYBOOK_TYPE_ISSUE),
      [daybookData],
   );

   // ==================== Callbacks ====================

   /**
    * Clears all form data and resets to empty state
    */
   const clearFormData = useCallback(() => {
      setRows(EMPTY_FORM_STATE);
   }, []);

   /**
    * Handles modal close event
    */
   const handleOnClose = useCallback(() => {
      clearFormData();
      onClose();
   }, [clearFormData, onClose]);

   /**
    * Handles form field changes
    * @param {string} name - Field name
    * @param {*} value - Field value
    */
   const handleChange = useCallback((name, value) => {
      setRows((prev) => ({
         ...prev,
         [name]: value,
      }));
   }, []);

   /**
    * Handles keyboard navigation between form fields
    * @param {KeyboardEvent} e - Keyboard event
    * @param {Object} nextRef - Reference to next field
    * @param {Object} prevRef - Reference to previous field
    */
   const handleKeyDown = useCallback((e, nextRef, prevRef) => {
      if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
         e.preventDefault();
         nextRef?.current?.focus();
      }
      if (e.key === 'Tab' && e.shiftKey) {
         e.preventDefault();
         prevRef?.current?.focus();
      }
   }, []);

   /**
    * Sets the challan number based on last daybook entry
    */
   const setChallanNumber = useCallback(() => {
      if (isOpen && lastDaybook?.docno) {
         const currentDocNo = String(lastDaybook.docno ?? 0);
         const nextDocNo = (parseInt(currentDocNo, 10) + 1).toString();
         setRows((prev) => ({
            ...prev,
            daybookId: lastDaybook.daybookId,
            challanno: nextDocNo,
         }));
      } else {
         setRows((prev) => ({
            ...prev,
            challanno: DEFAULT_CHALLAN_NUMBER,
         }));
      }
   }, [isOpen, lastDaybook]);

   /**
    * Handles form submission and creates issue inventory records
    */
   const handleSave = useCallback(async () => {
      try {
         setIsLoading(true);

         console.log("row data", rows, issueData);

         // Build header payload
         const header = {
            basicAmount: issueData?.basicAmount ?? 0,
            voucherId: 0,
            netAmount: issueData?.netAmount,
            companyId: Number(user?.loginhistory?.companyId),
            accountId: rows.millId,
            daybookId: rows.daybookId,
            additionAmount: issueData?.additionAmount,
            deductionAmount: issueData?.deductionAmount,
            dueDays: issueData?.dueDays,
            // transactionAt: moment(rows.issueAt).format(DATE_FORMAT),
            transactionAt: rows.issueAt,
            dueAt: moment(issueData?.dueAt).format(DATE_FORMAT),
            gstAmount: issueData?.gstAmount,
            warehouseId: issueData?.warehouseId,
            taxableAmount: issueData?.taxableAmount,
            billtoStateId: issueData?.billtoStateId,
            shiptoStateId: issueData?.shiptoStateId,
            gstTxnType: 0,
            gstSubTxnType: 0,
            assesseType: 0,
            distance: 0,
            brokerId: rows.brokerId ?? null,
            notes: null,
            transportId: null,
            vehicleId: null,
            representativeId: issueData?.representativeId,
         };

         // Build line items
         const lineItem = issueData.lineitems?.map((item) => {
            const selectedItem = itemData?.find((i) => i.id.toString() === item.itemId?.toString());
            return buildLineItem(item, selectedItem, rows.jobrate, rows.orderId);
         });

         const basePayload = { header, lineItem, billTerm: [] };

         // Split payload if multiple challans
         let payloads = [];
         if (rows.noofchallan > 1) {
            payloads = splitPayload(basePayload, rows.noofchallan);
         } else {
            payloads = [basePayload];
         }

         // Calculate taxes and assign document numbers
         const processedData = payloads.map((payload, idx) => {
            payload.header.docno = `${Number(rows.challanno) + idx}`;
            const result = calculateTaxAmount(payload, {
               company,
               user,
               itemData,
               billTermData,
               daybookData,
            });
            return result.data;
         });

         // Dispatch the action
         await dispatch(addManyIssueInventory(processedData));

         // Clean up and navigate
         clearFormData();
         handleOnClose();
         navigate('/admin/purchase/invoice');
      } catch (error) {
         console.error('Error saving issue to mill:', error);
      } finally {
         setIsLoading(false);
      }
   }, [
      issueData,
      user,
      rows,
      itemData,
      company,
      billTermData,
      daybookData,
      dispatch,
      clearFormData,
      handleOnClose,
      navigate,
   ]);

   // ==================== Effects ====================

   // Focus first field when modal opens
   useEffect(() => {
      if (isOpen) {
         const timer = setTimeout(() => {
            daybookRef.current?.focus();
         }, FOCUS_DELAY_MS);
         return () => clearTimeout(timer);
      }
   }, [isOpen]);

   // Fetch daybooks when modal opens
   useEffect(() => {
      if (isOpen && user) {
         dispatch(getAllDaybooks({ type: DAYBOOK_TYPE_ISSUE, companyId: user.loginhistory.companyId }));
      }
   }, [dispatch, isOpen, user]);

   // Set challan number when daybook changes
   useEffect(() => {
      setChallanNumber();
   }, [setChallanNumber]);

   // ==================== Render ====================
   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} size="6xl" isCentered closeOnOverlayClick={false}>
         <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
         <ModalContent
            bg={useColorModeValue('white', 'gray.800')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="16px"
            boxShadow="2xl"
            overflow="hidden"
            maxH="90vh">
            {/* Modal Header */}
            <ModalHeader
               bgGradient={useColorModeValue(
                  'linear(to-r, blue.500, blue.600)',
                  'linear(to-r, blue.600, blue.700)',
               )}
               color="white"
               py={6}
               px={8}
               fontSize="24px"
               fontWeight="700"
               letterSpacing="tight"
               borderBottom="4px solid"
               borderColor={useColorModeValue('blue.600', 'blue.500')}>
               <HStack spacing={3}>
                  <Box
                     bg="whiteAlpha.300"
                     p={2}
                     borderRadius="lg"
                     display="inline-flex"
                     alignItems="center"
                     justifyContent="center">
                     <Text fontSize="20px">📦</Text>
                  </Box>
                  <Text>Issue To Mill</Text>
               </HStack>
            </ModalHeader>

            <ModalCloseButton color="white" size="lg" _hover={{ bg: 'whiteAlpha.300' }} top={5} right={6} />

            {/* Modal Body */}
            <ModalBody p={8} overflowY="auto">
               <VStack spacing={6} align="stretch">
                  {/* Document Information Section */}
                  <Box
                     bg={useColorModeValue('gray.50', 'gray.700')}
                     p={6}
                     borderRadius="12px"
                     border="1px solid"
                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                     boxShadow="sm"
                     transition="all 0.2s"
                     _hover={{ boxShadow: 'md' }}>
                     <Text
                        fontSize="16px"
                        fontWeight="700"
                        mb={5}
                        color={useColorModeValue('blue.600', 'blue.300')}
                        textTransform="uppercase"
                        letterSpacing="wide">
                        Document Information
                     </Text>
                     <Grid gap={5} templateColumns="repeat(3, 1fr)">
                        <GridItem w="100%">
                           {renderDynamicDropdown(
                              'daybookId',
                              'Daybook',
                              filteredDaybookData,
                              {
                                 errorName: 'header.daybookId',
                                 value: rows.daybookId,
                                 onChange: (value) => handleChange('daybookId', value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                                 ref: daybookRef,
                                 nextRef: docNoRef,
                                 showCustomNoOptions: true,
                                 openfrom: 'salesdaybook',
                              },
                              {},
                              textColor,
                              false,
                              'Select Daybook',
                           )}
                        </GridItem>
                        <GridItem w="100%">
                           <InputField
                              ref={docNoRef}
                              type="text"
                              name="noofchallan"
                              label="Nos. of Challan"
                              value={rows.noofchallan}
                              onChange={(e) => handleChange('noofchallan', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, transactionAtRef, daybookRef)}
                           />
                        </GridItem>
                        <GridItem w="100%">
                           <InputField
                              ref={transactionAtRef}
                              type="date"
                              name="issueAt"
                              label="Issue Date"
                              value={rows.issueAt}
                              onChange={(e) => handleChange('issueAt', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, millRef, docNoRef)}
                           />
                        </GridItem>
                     </Grid>
                  </Box>

                  {/* Mill & Challan Details Section */}
                  <Box
                     bg={useColorModeValue('gray.50', 'gray.700')}
                     p={6}
                     borderRadius="12px"
                     border="1px solid"
                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                     boxShadow="sm"
                     transition="all 0.2s"
                     _hover={{ boxShadow: 'md' }}>
                     <Text
                        fontSize="16px"
                        fontWeight="700"
                        mb={5}
                        color={useColorModeValue('blue.600', 'blue.300')}
                        textTransform="uppercase"
                        letterSpacing="wide">
                        Mill & Challan Details
                     </Text>
                     <Grid gap={5} templateColumns="repeat(3, 1fr)">
                        <GridItem w="100%">
                           {renderDynamicDropdown(
                              'millId',
                              'Mill',
                              brokerData,
                              {
                                 errorName: 'header.millId',
                                 value: rows.millId,
                                 onChange: (value) => handleChange('millId', value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                                 ref: millRef,
                                 nextRef: challanNoRef,
                                 prevRef: transactionAtRef,
                                 showCustomNoOptions: true,
                                 openfrom: 'account',
                              },
                              {},
                              textColor,
                              false,
                              'Select Mill',
                           )}
                        </GridItem>
                        <GridItem w="100%">
                           <InputField
                              ref={challanNoRef}
                              type="text"
                              name="challanno"
                              label="Challan Number"
                              value={rows.challanno}
                              onChange={(e) => handleChange('challanno', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, orderRef, millRef)}
                           />
                        </GridItem>
                        <GridItem w="100%">
                           {renderDynamicDropdown(
                              'orderId',
                              'Take Order',
                              ORDER_DATA,
                              {
                                 errorName: 'header.orderId',
                                 value: rows.orderId,
                                 onChange: (value) => handleChange('orderId', value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                                 ref: orderRef,
                                 nextRef: brokerRef,
                                 prevRef: challanNoRef,
                              },
                              {},
                              textColor,
                              false,
                              'Select Order Type',
                           )}
                        </GridItem>
                     </Grid>
                  </Box>

                  {/* Broker & Additional Information Section */}
                  <Box
                     bg={useColorModeValue('gray.50', 'gray.700')}
                     p={6}
                     borderRadius="12px"
                     border="1px solid"
                     borderColor={useColorModeValue('gray.200', 'gray.600')}
                     boxShadow="sm"
                     transition="all 0.2s"
                     _hover={{ boxShadow: 'md' }}>
                     <Text
                        fontSize="16px"
                        fontWeight="700"
                        mb={5}
                        color={useColorModeValue('blue.600', 'blue.300')}
                        textTransform="uppercase"
                        letterSpacing="wide">
                        Broker & Additional Information
                     </Text>
                     <Grid gap={5} templateColumns="repeat(3, 1fr)">
                        <GridItem w="100%">
                           {renderDynamicDropdown(
                              'brokerId',
                              'Broker',
                              brokerData,
                              {
                                 errorName: 'header.brokerId',
                                 value: rows.brokerId,
                                 onChange: (value) => handleChange('brokerId', value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                                 ref: brokerRef,
                                 nextRef: jobrateRef,
                                 prevRef: orderRef,
                                 showCustomNoOptions: true,
                                 openfrom: 'account',
                              },
                              {},
                              textColor,
                              false,
                              'Select Broker',
                           )}
                        </GridItem>
                        <GridItem w="100%">
                           <InputField
                              ref={jobrateRef}
                              type="text"
                              name="jobrate"
                              label="Job Rate"
                              value={rows.jobrate}
                              onChange={(e) => handleChange('jobrate', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, remarksRef, brokerRef)}
                           />
                        </GridItem>
                        <GridItem w="100%">
                           <InputField
                              ref={remarksRef}
                              type="text"
                              name="remarks"
                              label="Remarks"
                              value={rows.remarks}
                              onChange={(e) => handleChange('remarks', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, submitRef, jobrateRef)}
                           />
                        </GridItem>
                     </Grid>
                  </Box>
               </VStack>
            </ModalBody>

            <Divider borderColor={useColorModeValue('gray.200', 'gray.600')} />

            {/* Modal Footer */}
            <ModalFooter
               bg={useColorModeValue('gray.50', 'gray.900')}
               py={5}
               px={8}
               borderTop="1px solid"
               borderColor={useColorModeValue('gray.200', 'gray.700')}>
               <HStack spacing={3} width="100%" justifyContent="flex-end">
                  <Button
                     variant="outline"
                     colorScheme="gray"
                     onClick={handleOnClose}
                     size="lg"
                     px={8}
                     fontWeight="600"
                     borderWidth="2px"
                     _hover={{
                        bg: useColorModeValue('gray.100', 'gray.700'),
                        transform: 'translateY(-1px)',
                        boxShadow: 'md',
                     }}
                     _focus={{
                        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                        borderColor: 'blue.500',
                     }}
                     transition="all 0.2s">
                     Cancel
                  </Button>
                  <Button
                     ref={submitRef}
                     colorScheme="blue"
                     onClick={handleSave}
                     size="lg"
                     px={8}
                     fontWeight="600"
                     isDisabled={isLoading}
                     isLoading={isLoading}
                     bgGradient={useColorModeValue(
                        'linear(to-r, blue.500, blue.600)',
                        'linear(to-r, blue.600, blue.700)',
                     )}
                     _hover={{
                        bgGradient: useColorModeValue(
                           'linear(to-r, blue.600, blue.700)',
                           'linear(to-r, blue.700, blue.800)',
                        ),
                        transform: 'translateY(-1px)',
                        boxShadow: 'lg',
                     }}
                     _focus={{
                        boxShadow: '0 0 0 4px rgba(66, 153, 225, 0.6)',
                        outline: 'none',
                     }}
                     _active={{
                        transform: 'translateY(0)',
                     }}
                     transition="all 0.2s"
                     boxShadow="md">
                     Save Issue
                  </Button>
               </HStack>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

// ==================== PropTypes ====================
IssueToMill.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   brokerData: PropTypes.arrayOf(
      PropTypes.shape({
         id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
         label: PropTypes.string,
         value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
   ),
   issueData: PropTypes.shape({
      basicAmount: PropTypes.number,
      netAmount: PropTypes.number,
      additionAmount: PropTypes.number,
      deductionAmount: PropTypes.number,
      dueDays: PropTypes.number,
      dueAt: PropTypes.string,
      gstAmount: PropTypes.number,
      warehouseId: PropTypes.number,
      taxableAmount: PropTypes.number,
      billtoStateId: PropTypes.number,
      shiptoStateId: PropTypes.number,
      representativeId: PropTypes.number,
      lineitems: PropTypes.arrayOf(PropTypes.object),
   }),
};

IssueToMill.defaultProps = {
   brokerData: [],
   issueData: {},
};

export default IssueToMill;
